import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../../api/client';
import { 
  Target, 
  Building2, 
  Package, 
  GitBranch, 
  BarChart3,
  TrendingUp,
  Settings,
  ChevronRight,
  PieChart,
  Columns,
  RefreshCw,
  ExternalLink,
  Database,
  FileSpreadsheet,
  Clock,
  Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import CompetitorManagement from './CompetitorManagement';
import PartTypeManagement from './PartTypeManagement';
import SegmentRuleManagement from './SegmentRuleManagement';
import MarketShareDashboard from './MarketShareDashboard';
import CompetitorKanban from './CompetitorKanban';

// Static components moved outside the main component
const StatCard = ({ icon: Icon, title, value, subtitle, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
    slate: 'bg-slate-50 text-slate-600'
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-2xl font-semibold text-slate-900 mt-1">{value}</p>
            {subtitle && (
              <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const QuickActionCard = ({ icon: Icon, title, description, onClick }) => (
  <Card 
    className="cursor-pointer hover:shadow-md transition-shadow group"
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-center gap-4">
        <div className="p-2 rounded-lg bg-slate-100 group-hover:bg-slate-200 transition-colors">
          <Icon className="h-5 w-5 text-slate-600" />
        </div>
        <div className="flex-1">
          <p className="font-medium text-slate-900">{title}</p>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600 transition-colors" />
      </div>
    </CardContent>
  </Card>
);

const SetupStep = ({ number, title, description, isComplete, isOptional, onClick }) => (
  <div 
    className={`flex items-start gap-4 p-4 rounded-lg border ${onClick ? 'cursor-pointer hover:bg-slate-50' : ''} ${isComplete ? 'bg-green-50 border-green-200' : 'bg-white'}`}
    onClick={onClick}
  >
    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
      isComplete 
        ? 'bg-green-500 text-white' 
        : 'bg-slate-200 text-slate-600'
    }`}>
      {isComplete ? <Check className="h-4 w-4" /> : number}
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <p className={`font-medium ${isComplete ? 'text-green-700' : 'text-slate-900'}`}>
          {title}
        </p>
        {isOptional && (
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">Optional</span>
        )}
      </div>
      <p className={`text-sm ${isComplete ? 'text-green-600' : 'text-slate-500'}`}>
        {description}
      </p>
    </div>
    {onClick && (
      <ChevronRight className={`h-5 w-5 ${isComplete ? 'text-green-400' : 'text-slate-400'}`} />
    )}
  </div>
);

const CompetitiveIntelligencePage = () => {
  // Land on Market Share by default - it's the headline view for the sales audience.
  const [activeTab, setActiveTab] = useState('market-share');

  // Fetch dashboard summary
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['ci-dashboard-summary'],
    queryFn: async () => {
      const response = await apiClient.get('/api/competitive-intelligence/dashboard-summary');
      return response.data;
    }
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-100">
              <Target className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Competitive Intelligence</h1>
              <p className="text-sm text-slate-500">Track competitors, market share, and industry trends</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="competitors" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Competitors
            </TabsTrigger>
            <TabsTrigger value="part-types" className="flex items-center gap-2">
              <Package className="h-4 w-4" />
              Part Types
            </TabsTrigger>
            <TabsTrigger value="segment-rules" className="flex items-center gap-2">
              <GitBranch className="h-4 w-4" />
              Segment Rules
            </TabsTrigger>
            <TabsTrigger value="market-share" className="flex items-center gap-2">
              <PieChart className="h-4 w-4" />
              Market Share
            </TabsTrigger>
            <TabsTrigger value="kanban" className="flex items-center gap-2">
              <Columns className="h-4 w-4" />
              Kanban (Advanced)
            </TabsTrigger>
            <TabsTrigger value="reconciliation" className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Data Reconciliation
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Building2}
                title="Competitors"
                value={summaryLoading ? '...' : summary?.competitors_count || 0}
                subtitle="Active competitors tracked"
                color="blue"
              />
              <StatCard
                icon={Package}
                title="Part Types"
                value={summaryLoading ? '...' : summary?.part_types_count || 0}
                subtitle="Business segments defined"
                color="green"
              />
              <StatCard
                icon={GitBranch}
                title="Segment Rules"
                value={summaryLoading ? '...' : summary?.segment_rules_count || 0}
                subtitle="Competitor assignments"
                color="purple"
              />
              <StatCard
                icon={Target}
                title="IHS Programs"
                value={summaryLoading ? '...' : (summary?.ihs_carlines_count || 0).toLocaleString()}
                subtitle={`${summary?.distinct_oems || 0} OEMs`}
                color="amber"
              />
            </div>

            {/* Quick Actions */}
            <div>
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <QuickActionCard
                  icon={Building2}
                  title="Add Competitor"
                  description="Register a new competitor in the system"
                  onClick={() => setActiveTab('competitors')}
                />
                <QuickActionCard
                  icon={Package}
                  title="Define Part Type"
                  description="Create a new business segment category"
                  onClick={() => setActiveTab('part-types')}
                />
                <QuickActionCard
                  icon={GitBranch}
                  title="Create Segment Rule"
                  description="Assign competitors to OEM/Region/Part combinations"
                  onClick={() => setActiveTab('segment-rules')}
                />
                <QuickActionCard
                  icon={TrendingUp}
                  title="View Market Share"
                  description="Analyze market position across segments"
                  onClick={() => setActiveTab('market-share')}
                />
              </div>
            </div>

            {/* Setup Progress */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Settings className="h-5 w-5 text-slate-400" />
                  Setup Progress
                </CardTitle>
                <CardDescription>
                  Complete these steps to enable market share analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <SetupStep 
                    number={1}
                    title="Add Competitors"
                    description="Register your key competitors"
                    isComplete={(summary?.competitors_count || 0) > 0}
                    onClick={() => setActiveTab('competitors')}
                  />
                  <SetupStep 
                    number={2}
                    title="Define Part Types"
                    description="Create your business segment categories"
                    isComplete={(summary?.part_types_count || 0) > 0}
                    onClick={() => setActiveTab('part-types')}
                  />
                  <SetupStep 
                    number={3}
                    title="Create Segment Rules"
                    description="Assign competitors to OEM/Part/Region combinations"
                    isComplete={(summary?.segment_rules_count || 0) > 0}
                    onClick={() => setActiveTab('segment-rules')}
                  />
                  <SetupStep 
                    number={4}
                    title="Link Opportunities"
                    description="Connect your opportunities to part types"
                    isComplete={false}
                    isOptional
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Competitors Tab */}
          <TabsContent value="competitors">
            <CompetitorManagement />
          </TabsContent>

          {/* Part Types Tab */}
          <TabsContent value="part-types">
            <PartTypeManagement />
          </TabsContent>

          {/* Segment Rules Tab */}
          <TabsContent value="segment-rules">
            <SegmentRuleManagement />
          </TabsContent>

          {/* Market Share Tab */}
          <TabsContent value="market-share">
            <MarketShareDashboard />
          </TabsContent>

          {/* Kanban Board Tab */}
          <TabsContent value="kanban">
            <CompetitorKanban />
          </TabsContent>

          {/* Data Reconciliation Tab - Links to standalone page */}
          <TabsContent value="reconciliation">
            <div className="space-y-6">
              {/* Hero Card - Link to IHS Data Management */}
              <Card className="border-2 border-blue-100 bg-gradient-to-br from-blue-50 to-slate-50">
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="p-4 rounded-xl bg-blue-100">
                      <Database className="h-8 w-8 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-slate-900 mb-2">
                        IHS Data Management
                      </h2>
                      <p className="text-slate-600 mb-4 max-w-xl">
                        Upload your monthly IHS market data files to discover new opportunities, 
                        track program changes, and keep your competitive intelligence up to date.
                      </p>
                      
                      {/* Features list */}
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
                          <span>Upload Excel files</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <RefreshCw className="h-4 w-4 text-blue-500" />
                          <span>Auto-detect changes</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Clock className="h-4 w-4 text-amber-500" />
                          <span>Full audit history</span>
                        </div>
                      </div>
                      
                      <Link to="/volume-management">
                        <Button size="lg" className="gap-2">
                          Open IHS Data Management
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Info Cards */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-emerald-100">
                        <TrendingUp className="h-4 w-4 text-emerald-600" />
                      </div>
                      <h3 className="font-medium text-slate-900">New Opportunities</h3>
                    </div>
                    <p className="text-sm text-slate-500">
                      Discover new market programs and decide whether to track them or create opportunities.
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-amber-100">
                        <RefreshCw className="h-4 w-4 text-amber-600" />
                      </div>
                      <h3 className="font-medium text-slate-900">Program Updates</h3>
                    </div>
                    <p className="text-sm text-slate-500">
                      Review volume changes, date shifts, and other updates to existing programs.
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-slate-100">
                        <Target className="h-4 w-4 text-slate-600" />
                      </div>
                      <h3 className="font-medium text-slate-900">Severity Ratings</h3>
                    </div>
                    <p className="text-sm text-slate-500">
                      Smart prioritization highlights high-impact changes that need your attention.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CompetitiveIntelligencePage;
